import { memo, useEffect, useRef } from 'react';
import { Vector3 } from 'three';

import {
  CameraControls as BaseCameraControls,
  CameraControlsProps as BaseCameraControlsProps,
  useDollhouse,
  useInteractiveObjects,
} from '@spazious/dollhouse-engine';
import { useStore } from '@spazious/dollhouse-store';
import { CameraControlsImpl } from '@spazious/dollhouse-three';
import { ControlsModeEnum } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

import { isEditableTarget } from '../../utils/index.js';
import { CameraTravelControls } from '../CameraTravelControls';

export interface CameraControlsProps extends BaseCameraControlsProps {}

export const CameraControls: React.FC<CameraControlsProps> = memo(({ ...restProps }) => {
  const controlsRef = useRef<CameraControlsImpl>(null!);
  const set = useStore(({ set }) => set);
  const controls = useStore(({ controls }) => controls);
  const camera = useStore(({ camera }) => camera);
  const world = useDollhouse(({ world }) => world);
  const raycaster = useDollhouse(({ raycaster }) => raycaster);
  const [interactiveObjects] = useInteractiveObjects();

  const walkTest = useEventCallback(() => {
    // three-mesh-bvh's accelerated raycast stops at the first hit per object
    // when firstHitOnly is set; we only need to know whether anything was hit
    const { firstHitOnly } = raycaster;
    raycaster.firstHitOnly = true;
    const intersects = raycaster.intersectObjects(interactiveObjects);
    raycaster.firstHitOnly = firstHitOnly;
    return intersects.length === 0;
  });

  const handleEnd = useEventCallback(() => {
    if (controlsRef.current && controls) {
      const pos = new Vector3();
      controlsRef.current.getPosition(pos);
      set((state) => ({
        ...state,
        camera: {
          ...state.camera,
          position: pos.toArray(),
          azimuthAngle: controlsRef.current.azimuthAngle,
          polarAngle: controlsRef.current.polarAngle,
          fov: (camera as any).fov ?? state.camera.fov,
        },
      }));
    }
  });

  useEffect(() => {
    const onDocumentKey = (e: KeyboardEvent) => {
      // Ignore the shortcut while typing (e.g. writing notes), otherwise
      // pressing "p" in a text field would switch to walk mode.
      if (isEditableTarget(e.target)) {
        return;
      }
      if (e.key === 'p') {
        set({
          controls: ControlsModeEnum.Travelling,
        });
      }
    };
    document.addEventListener('keydown', onDocumentKey);
    return () => {
      document.removeEventListener('keydown', onDocumentKey);
    };
  }, [set]);

  if (controls === ControlsModeEnum.Travelling) {
    if (world?.navMesh) {
      return <CameraTravelControls makeDefault />;
    }
  }

  return (
    <BaseCameraControls
      ref={controlsRef}
      enablePan={camera.enablePan ?? true}
      view={controls as 'orbit' | 'zenithal' | 'walk'}
      world={world}
      {...restProps}
      makeDefault
      walkTest={walkTest}
      onEnd={handleEnd}
    />
  );
});
